/**
 * The relationship graph, drawn with Cytoscape.
 *
 * **Clicking an edge opens the assertion behind it.** That is the M6 exit criterion —
 * evidence in at most two clicks — and it is why every edge in the payload carries an
 * `assertion_id` the type system will not let a renderer omit.
 *
 * Layout is `cose` rather than anything cleverer: a force-directed layout on a case-sized
 * graph is legible, deterministic enough to re-recognise between visits, and does not
 * need a second dependency.
 */

import { useEffect, useRef, useState, type ReactElement } from 'react';
import cytoscape, { type Core, type EdgeSingular } from 'cytoscape';
import { useQuery } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { GraphEdge, GraphResponse } from '@/lib/schemas';
import { Badge, ConfidenceBadge, Drawer, ErrorState, Loading, ProvenanceLink } from '@/components/ui';

const NODE_COLOUR: Record<string, string> = {
  entity: '#60a5fa',
  email: '#34d399',
  username: '#a78bfa',
  organisation: '#fbbf24',
  domain: '#f472b6',
  place: '#22d3ee',
  identifier: '#94a3b8',
  url: '#c084fc',
  work: '#fda4af',
  other: '#a8a29e',
};

/** The graph tab for one case. */
export function GraphTab({ caseId }: { caseId: string }): ReactElement {
  const container = useRef<HTMLDivElement | null>(null);
  const instance = useRef<Core | null>(null);
  const [selected, setSelected] = useState<GraphEdge | null>(null);

  const graph = useQuery({
    queryKey: ['graph', caseId],
    queryFn: () => request<GraphResponse>(`/cases/${caseId}/graph`),
  });

  useEffect(() => {
    if (!container.current || !graph.data) return undefined;

    const core = cytoscape({
      container: container.current,
      elements: [
        ...graph.data.nodes.map((node) => ({
          data: {
            id: node.id,
            label: node.label,
            kind: node.kind,
            // Degree centrality drives node size, so the shape of a case is visible
            // before any label is read.
            size: 18 + node.centrality * 40,
            primary: node.is_primary_subject,
          },
        })),
        ...graph.data.edges.map((edge, index) => ({
          data: {
            id: `e${index}`,
            source: edge.source,
            target: edge.target,
            label: edge.predicate,
            assertionId: edge.assertion_id,
            index,
          },
        })),
      ],
      style: [
        {
          selector: 'node',
          style: {
            'background-color': (element: cytoscape.NodeSingular) =>
              NODE_COLOUR[element.data('kind') as string] ?? NODE_COLOUR.other!,
            label: 'data(label)',
            'font-size': '9px',
            color: '#a8a29e',
            width: 'data(size)',
            height: 'data(size)',
            'text-valign': 'bottom',
            'text-margin-y': 4,
          },
        },
        {
          selector: 'node[?primary]',
          style: { 'border-width': 3, 'border-color': '#f8fafc' },
        },
        {
          selector: 'edge',
          style: {
            width: 1,
            'line-color': '#57534e',
            'curve-style': 'bezier',
            label: 'data(label)',
            'font-size': '7px',
            color: '#78716c',
          },
        },
      ],
      layout: { name: 'cose', animate: false },
    });

    core.on('tap', 'edge', (event) => {
      const edge = event.target as EdgeSingular;
      const index = edge.data('index') as number;
      setSelected(graph.data.edges[index] ?? null);
    });

    instance.current = core;
    return () => {
      core.destroy();
      instance.current = null;
    };
  }, [graph.data]);

  if (graph.isLoading) return <Loading label="Projecting the graph" />;
  if (graph.error) return <ErrorState error={graph.error} />;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 text-xs text-muted">
        <span>
          {graph.data?.nodes.length ?? 0} nodes · {graph.data?.edges.length ?? 0} edges
        </span>
        {graph.data?.cached && <Badge>cached</Badge>}
        {graph.data?.truncated && (
          <Badge tone="warn">
            truncated at {graph.data.node_cap} nodes — this is not the whole case
          </Badge>
        )}
        <span>Click an edge to open the evidence behind it.</span>
      </div>
      <div
        ref={container}
        data-testid="graph-canvas"
        className="h-[32rem] w-full rounded-lg border border-line bg-raised"
      />
      <Drawer
        open={selected !== null}
        title="Relationship"
        onClose={() => setSelected(null)}
      >
        {selected && (
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-xs uppercase text-muted">Claim</dt>
              <dd className="font-mono">{selected.predicate}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-muted">Confidence</dt>
              <dd>
                <ConfidenceBadge
                  sourceReliability={selected.source_reliability}
                  informationCredibility={selected.information_credibility}
                  extractionConfidence={selected.extraction_confidence}
                />
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-muted">Evidence</dt>
              <dd>
                <ProvenanceLink caseId={caseId} assertionId={selected.assertion_id} />
              </dd>
            </div>
          </dl>
        )}
      </Drawer>
    </div>
  );
}
